import { useState, useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/useAuthStore";
import { authService } from "@/services/authService";
import { Sidebar, Topbar, CommandPalette } from "@/components/AppShell";
import { useTenants } from "@/hooks/useTenants";

export default function PrivateLayout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const { data: tenants = [] } = useTenants();

  const handleLogout = async () => {
    await authService.logout();
    clearAuth();
    navigate("/login");
  };

  // ⌘K shortcut
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // Breadcrumbs
  const crumbs = useMemo(() => {
    const p = location.pathname;
    if (p === "/") return [{ label: "Dashboard" }];
    if (p === "/tenants")
      return [
        { label: "Dashboard", onClick: () => navigate("/") },
        { label: "Tenants" },
      ];
    if (p.startsWith("/tenants/")) {
      const id = p.split("/")[2];
      const t = tenants.find((x) => String(x.id) === id);
      return [
        { label: "Dashboard", onClick: () => navigate("/") },
        { label: "Tenants", onClick: () => navigate("/tenants") },
        { label: t?.name || "Detail" },
      ];
    }
    return [{ label: "Ops Portal" }];
  }, [location.pathname, tenants, navigate]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const user = { name: "Ops Admin", role: "Administrator" };

  return (
    <div className="flex h-screen overflow-hidden bg-gray7">
      <Sidebar
        onLogout={handleLogout}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <div className="flex flex-1 flex-col min-w-0">
        <Topbar
          crumbs={crumbs}
          onMenu={() => setMobileOpen(true)}
          onOpenCmd={() => setCmdOpen(true)}
          user={user}
        />
        <main className="flex-1 overflow-y-auto p-5 lg:p-6">
          <Outlet />
        </main>
      </div>
      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        tenants={tenants}
        onNavigate={(p) => navigate(p)}
      />
    </div>
  );
}
